<template>
  <Transition :name="transitionName">
    <div v-if="isVisible" class="toast-notification" :class="[toastTypeClass, positionClass]">
      <div class="toast-content">
        <span class="toast-message">{{ message }}</span>
      </div>
      <button class="toast-close" @click="closeToast" aria-label="Close">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
  </Transition>
</template>

<script>
export default {
  name: 'ToastV2',
  data() {
    return {
      isVisible: false,
      message: '',
      type: 'success',
      position: 'bottom-right',
      duration: 3000,
      timer: null
    };
  },
  computed: {
    toastTypeClass() {
      return `toast-${this.type}`;
    },
    positionClass() {
      return `toast-position-${this.position}`;
    },
    transitionName() {
      // Use different transitions based on position
      const transitionMap = {
        'top-left': 'toast-slide-left',
        'top-center': 'toast-slide-top',
        'top-right': 'toast-slide-right',
        'bottom-left': 'toast-slide-left',
        'bottom-center': 'toast-slide-bottom',
        'bottom-right': 'toast-slide-right',
        'center': 'toast-slide-bottom'
      };
      return transitionMap[this.position] || 'toast-fade';
    }
  },
  methods: {
    show(message, type = 'success', duration = 3000, position = 'bottom-right') {
      // Clear any existing timer
      if (this.timer) {
        clearTimeout(this.timer);
      }

      this.message = message;
      this.type = type;
      this.duration = duration;
      this.position = position;
      this.isVisible = true;

      // Auto close after duration
      this.timer = setTimeout(() => {
        this.closeToast();
      }, this.duration);
    },
    success(message, duration = 3000, position = 'bottom-right') {
      this.show(message, 'success', duration, position);
    },
    error(message, duration = 3000, position = 'bottom-right') {
      this.show(message, 'error', duration, position);
    },
    warning(message, duration = 3000, position = 'bottom-right') {
      this.show(message, 'warning', duration, position);
    },
    info(message, duration = 3000, position = 'bottom-right') {
      this.show(message, 'info', duration, position);
    },
    closeToast() {
      this.isVisible = false;
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    }
  },
  beforeDestroy() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
  }
};
</script>

<style scoped>
.toast-notification {
  position: fixed;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 400px;
  max-width: 90vw;
  padding: 18px 24px;
  border-radius: 12px;
  box-shadow: 0px 7px 15px 2px rgba(0, 0, 0, 0.1);
  font-family: 'Roboto', sans-serif;
}

/* Position variations */
.toast-position-top-left {
  top: 20px;
  left: 20px;
}

.toast-position-top-center {
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
}

.toast-position-top-right {
  top: 20px;
  right: 20px;
}

.toast-position-bottom-left {
  bottom: 50px;
  left: 20px;
}

.toast-position-bottom-center {
  bottom: 50px;
  left: 50%;
  transform: translateX(-50%);
}

.toast-position-bottom-right {
  bottom: 50px;
  right: 20px;
}

.toast-position-center {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.toast-content {
  flex: 1;
  display: flex;
  align-items: center;
}

.toast-message {
  font-size: 16px;
  font-weight: 500;
  color: #ffffff;
  white-space: pre-wrap;
  word-break: break-word;
}

.toast-close {
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  margin-left: 16px;
  opacity: 0.8;
  transition: opacity 0.2s;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-close:hover {
  opacity: 1;
}

.toast-close span {
  display: block;
  line-height: 1;
}

/* Toast type styles */
.toast-success {
  background: #508F30;
}

.toast-error {
  background: #f34e4e;
}

.toast-warning {
  background: #f7cc53;
}

.toast-info {
  background: #038edc;
}

/* Animations for slide from bottom (bottom-center, center) */
.toast-slide-bottom-enter-active,
.toast-slide-bottom-leave-active {
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.toast-slide-bottom-enter-from {
  opacity: 0;
}

.toast-position-bottom-center.toast-slide-bottom-enter-from {
  transform: translateX(-50%) translateY(150px);
}

.toast-position-center.toast-slide-bottom-enter-from {
  transform: translate(-50%, calc(-50% + 150px));
}

.toast-slide-bottom-enter-to {
  opacity: 1;
}

.toast-position-bottom-center.toast-slide-bottom-enter-to {
  transform: translateX(-50%) translateY(0);
}

.toast-position-center.toast-slide-bottom-enter-to {
  transform: translate(-50%, -50%);
}

.toast-slide-bottom-leave-from {
  opacity: 1;
}

.toast-position-bottom-center.toast-slide-bottom-leave-from {
  transform: translateX(-50%) translateY(0);
}

.toast-position-center.toast-slide-bottom-leave-from {
  transform: translate(-50%, -50%);
}

.toast-slide-bottom-leave-to {
  opacity: 0;
}

.toast-position-bottom-center.toast-slide-bottom-leave-to {
  transform: translateX(-50%) translateY(150px);
}

.toast-position-center.toast-slide-bottom-leave-to {
  transform: translate(-50%, calc(-50% + 150px));
}

/* Animations for slide from top (top-center) */
.toast-slide-top-enter-active,
.toast-slide-top-leave-active {
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.toast-slide-top-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(-150px);
}

.toast-slide-top-enter-to {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

.toast-slide-top-leave-from {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

.toast-slide-top-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-150px);
}

/* Animations for slide from right (top-right, bottom-right) */
.toast-slide-right-enter-active,
.toast-slide-right-leave-active {
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.toast-slide-right-enter-from {
  opacity: 0;
  transform: translateX(150px);
}

.toast-slide-right-enter-to {
  opacity: 1;
  transform: translateX(0);
}

.toast-slide-right-leave-from {
  opacity: 1;
  transform: translateX(0);
}

.toast-slide-right-leave-to {
  opacity: 0;
  transform: translateX(150px);
}

/* Animations for slide from left (top-left, bottom-left) */
.toast-slide-left-enter-active,
.toast-slide-left-leave-active {
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.toast-slide-left-enter-from {
  opacity: 0;
  transform: translateX(-150px);
}

.toast-slide-left-enter-to {
  opacity: 1;
  transform: translateX(0);
}

.toast-slide-left-leave-from {
  opacity: 1;
  transform: translateX(0);
}

.toast-slide-left-leave-to {
  opacity: 0;
  transform: translateX(-150px);
}

/* Fade animation for center position */
.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: all 0.3s ease;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.9);
}

.toast-fade-enter-to,
.toast-fade-leave-from {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
}

/* Responsive */
@media (max-width: 576px) {
  .toast-notification {
    min-width: 90vw;
  }
  
  .toast-position-top-left,
  .toast-position-top-right,
  .toast-position-bottom-left,
  .toast-position-bottom-right {
    left: 5%;
    right: 5%;
  }
  
  .toast-position-top-center,
  .toast-position-bottom-center {
    left: 50%;
    transform: translateX(-50%);
  }
  
  .toast-position-top-left,
  .toast-position-top-center,
  .toast-position-top-right {
    top: 10px;
  }
  
  .toast-position-bottom-left,
  .toast-position-bottom-center,
  .toast-position-bottom-right {
    bottom: 20px;
  }
  
  .toast-message {
    font-size: 14px;
  }
}
</style>
